<template>
  <div>
    <span class="d-flex mx-4 my-4">
      <v-spacer />
      <div class="mt-3 ml-8">
        <v-icon class="text-h4 mb-3">mdi-barn</v-icon>
        <span class="text-h5 ml-2">Farm: {{currentFarm}}</span>
        <div v-if="this.currentFarm == null">
          Please go to <a @click="goToSettingsPage()">settings</a>
          and set the default farm.
        </div>
      </div>
      <v-spacer />
      <v-btn
        fab
        color="accent"
        @click="addGroup()">
        <v-icon class="white--text text-h4">mdi-playlist-plus</v-icon>
      </v-btn>
    </span>

    <div class="d-flex flex-wrap justify-center">
      <v-card v-if="eggGroups.length === 0"
        class="ma-2 pa-10"
        width="356"
        color="cardbackground">
        <div>No Groups found.</div>
      </v-card>
      <v-card v-else v-for="(item, index) in eggGroups" :key="item.groupName + item.date"
          class="py-0 ma-2"
          max-width="342"
          color="cardbackground">
        <v-list-item class="pl-1">
          <v-list-item-content class="pt-1">
            <v-list-item-title class="text-h6 mb-0">
              <span>
                <v-btn icon @click="toggleCardDetails(index)">
                  <v-icon>{{ showDetail[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </span>
              <a @click="toggleCardDetails(index)">{{ item.groupName }}</a>
            </v-list-item-title>
            <v-list-item-subtitle class="ml-9">Egg Count: {{item.samples ? item.samples.length : 0}}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>

        <v-card-actions class="px-3 pb-3">
          <v-btn
            :disabled="item.farm != currentFarm"
            x-large
            color="primary"
            class="pl-1 pr-3 mr-1"
            @click="goToCollectionPage(item)">
              <v-icon color="#F8F0E3">mdi-egg</v-icon><span>Collect</span>
          </v-btn>

          <v-btn
            fab
            class="ml-1"
            color="warning"
            :disabled="item.farm != currentFarm || isUploadDisabled(item)"
            @click="selectedGroup = item; showUploadDialog = true">
            <v-icon>mdi-upload</v-icon>
          </v-btn>

          <v-btn
          :disabled="item.farm != currentFarm"
            fab
            color="success"
            @click="editGroup(item)">
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>

          <v-btn
              fab
              color="secondary"
              :disabled="isDeleteGroupDisabled(item)"
              @click="confirmDeleteGroup(item)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </v-card-actions>

        <v-expand-transition>
          <div v-show="showDetail[index]">
            <v-divider></v-divider>

            <v-card-text>
              <div class="primary--text text-subtitle-1">Details</div>
              <table class="ml-2">
                <tr><td><b class="pr-1">House:</b></td><td>{{item.farm}}-{{item.house}}</td></tr>
                <tr><td><b class="pr-1">Created:</b></td><td>{{formatDate(item.date)}}</td></tr>
                <tr><td><b class="pr-1">Operators:</b></td><td>{{getOperatorNames(item)}}</td></tr>
                <tr><td><b class="pr-1">Notes:</b></td><td>{{item.notes}}</td></tr>
                <tr><td><b class="pr-1">ClientUid:</b></td><td class="truncate">{{item.clientUid}}</td></tr>
              </table>
            </v-card-text>
          </div>
        </v-expand-transition>
      </v-card>
    </div>

    <v-dialog v-model="showModifyDialog" persistent max-width="400px">
      <v-card color="cardbackground">
        <v-card-title>
          <div class="headline" v-if="editItem.oldGroupName === null">Create Group</div>
          <div class="headline" v-else>Modify Group</div>
        </v-card-title>
        <v-card-text class="pt-2">
          <v-row dense>
            <v-col cols="6">
               <v-text-field
                readonly
                :value = "formatDate(editItem.date)"
                label="Collection Date"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                disabled
                v-model="editItem.farm"
                label="Farm"
              />
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="editItem.house"
                label="House *"
                :items="houses"
                :disabled="houses.length === 0"
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                clearable
                class="pt-0"
                v-model="editItem.notes"
                @keypress="allowAlphaNumOnly($event)"
                @keyup="editItem.notes = toAlphaNumOnly(editItem.notes)"
                label="Notes"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn
            color="primary"
            class="white--text mb-3"
            :disabled="!saveEnabled"
            @click="editItem.oldGroupName != null ? showModifyConfirmDialog = true : saveGroup()">
            Save
          </v-btn>
          <v-btn
            color="secondary"
            class="mb-3 mr-4"
            @click="closeModifyDialog()">
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

     <v-dialog v-model="showModifyConfirmDialog" persistent max-width="350">
      <v-card color="cardbackground">
        <v-card-title class="text-h5">Save Group Changes?</v-card-title>
        <v-card-text class="d-flex">
          <div>
            <div class="primary--text text-subtitle-1">Old</div>
            <ul class="pl-4 mr-2" style="min-width:145px; border-top:1px solid">
              <li><b>Farm:</b> {{oldModifyItem.farm}}</li>
              <li><b>House:</b> {{oldModifyItem.house}}</li>
              <li><b>Date:</b> {{oldModifyItem.date}}</li>
              <li><b>Notes:</b> {{oldModifyItem.notes}}</li>
            </ul>
          </div>
          <v-spacer />
          <div class="ml-2">
            <div class="primary--text text-subtitle-1">New</div>
            <ul class="pl-4 mr-2" style="min-width:145px; border-top:1px solid">
              <li :style="changeBackgroundColor(editItem.farm != oldModifyItem.farm )"><b>Farm:</b> {{editItem.farm}}</li>
              <li :style="changeBackgroundColor(editItem.house != oldModifyItem.house)"><b>House:</b> {{editItem.house}}</li>
              <li :style="changeBackgroundColor(editItem.date != oldModifyItem.date)"><b>Date:</b> {{editItem.date}}</li>
              <li :style="`${changeBackgroundColor(editItem.notes != oldModifyItem.notes)}`"><b>Notes:</b> {{editItem.notes}}</li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="saveGroup()">Save</v-btn>
          <v-btn color="secondary" @click="showModifyConfirmDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteDialog" persistent max-width="350">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Confirm Delete</v-card-title>
        <v-card-text>Is it okay to delete {{ groupToDelete }}?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" class="white--text" @click="deleteGroup()">Yes</v-btn>
          <v-btn color="secondary" @click="showDeleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showUploadDialog" persistent max-width="350">
      <v-card  color="cardbackground">
        <v-card-title class="text-h5">Confirm Upload</v-card-title>
        <v-card-text  v-if="selectedGroup">
          <div>Upload {{ selectedGroup.groupName }}?</div>
          <ul class="mt-2">
            <li><b>Egg Count:</b> {{ selectedGroup.samples.length }}</li>
            <li><b>Created:</b> {{ selectedGroup.date }}</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <div class="flex-grow-1"></div>
          <v-btn color="primary" class="white--text" @click="uploadEggGroup()">Upload</v-btn>
          <v-btn color="secondary" @click="showUploadDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay :value="isBusy">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import OfflineStorage from '../utils/offlineStorage'
import { mapState } from 'vuex'
import eggApi from '../api/eggApi'

export default {
  data() {
    return {
      deviceName: "",
      eggGroups: [],
      archivedEggGroups: [],
      isBusy: false,
      groupToDelete: null,
      showModifyDialog: false,
      showModifyConfirmDialog: false,
      showDeleteDialog: false,
      showUploadDialog: false,
      editItem: {},
      mgs: [],
      selectedGroup: null,
      showDetail: []
    }
  },

  async created() {
    try {
      this.deviceName = await OfflineStorage.getItem('deviceName');
      this.eggGroups = await OfflineStorage.getItem('eggGroups');
      if (this.eggGroups == null) this.eggGroups = [];

      this.archivedEggGroups = await OfflineStorage.getItem('archivedEggGroups');
      if (this.archivedEggGroups == null) this.archivedEggGroups = [];
      else await this.removeOldEggGroups();
    } catch(error) {
      this.$root.showNoticeError(error.message);
    }
  },

  computed: {
    ...mapState(['settings', 'isOnline']),
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    currentFarm() {
      return this.settings.currentFarm;
    },
    houses() {
      if (!this.settings.farmHouses) return [];
      var uniqueHouseNames = this.settings.farmHouses.filter(house => house.isEnabled)
           .map(house => house.houseCode)
           .filter((house, i, list) => list.indexOf(house) === i);
      return uniqueHouseNames;
    },
    oldModifyItem() {
      var oldItem = this.eggGroups.find(x => x.groupName === this.editItem.oldGroupName);
      return oldItem == null ? {} : oldItem;
    },
    saveEnabled() {
      const item = this.editItem;
      if (item.farm && item.house)
        return true;
      else
        return false;
    }
  },

  methods: {
    addGroup() {
      const date = new Date();
      this.editItem = {
        deviceName: this.deviceName,
        checkWingband: false,
        oldGroupName: null,
        groupName: null,
        operatorId: null,
        farm: this.currentFarm,
        house: null,
        date: date,
        notes: null,
        samples: []
      };

      this.showModifyDialog = true;
    },
    allowAlphaNumOnly(e) {
      let char = String.fromCharCode(e.keyCode); // Get the character
      if(/^[A-Za-z0-9 ]+$/.test(char)) return true; // Match with regex 
      else e.preventDefault(); // If not match, don't add to input text
    },
    changeBackgroundColor(isChanged) {
      if (!isChanged) return '';
      if (this.$vuetify.theme.dark)
        return 'background-color: gray';
      else
        return 'background-color:yellow';
    },
    closeModifyDialog() {
      this.showModifyDialog = false;
      this.showModifyConfirmDialog = false;
    },
    confirmDeleteGroup(item) {
      this.showDeleteDialog = true;
      this.groupToDelete = item.groupName;
    },
    daysDiff(firstDate, secondDate) {
      const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
      const diffDays = Math.round(Math.abs((firstDate - secondDate) / oneDay));
      return diffDays;
    },
    async deleteGroup() {
      try {
        const index = this.eggGroups.findIndex(x => x.groupName === this.groupToDelete);

        //* Add group to archived groups.
        this.eggGroups[index].deleteDate = new Date();
        this.archivedEggGroups.push(this.eggGroups[index]);
        await OfflineStorage.setItem('archivedEggGroups', this.archivedEggGroups);

        //* Remove group from active groups.
        this.eggGroups.splice(index, 1);
        await OfflineStorage.setItem('eggGroups', this.eggGroups);

        this.$root.showNoticeSuccess('Group Deleted Successfully');
        this.showDeleteDialog = false;
      } catch (error) {
        this.$root.showNoticeError(error.message);
      }
    },
    editGroup(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editItem.oldGroupName = this.editItem.groupName;
      // this.setAvailableMgs();
      this.showModifyDialog = true;
    },
    formatDate(dt) {
      dt = new Date(dt);

      const year  = dt.getFullYear();
      const month = dt.toLocaleDateString(undefined, { month: 'short'}).toUpperCase()
      const day   = dt.getDate().toString().padStart(2, "0");
      const date = `${year}-${month}-${day}`;

      return date;
    },
    getOperatorNames(item) {
      let uniqueOperatorIds = item.samples
        .filter((v,i,a)=>a.findIndex(t=>(t.operatorId === v.operatorId))===i)
        .map(e=>e.operatorId)
      let operatorNames = this.settings.farmOperators
        .filter(o => uniqueOperatorIds.includes(o.operatorId))
        .map(e=>e.operatorName);
      return operatorNames.join(", ");
    },
    goToCollectionPage(item) {
      this.$router.push({ name: 'Egg Collection', params: {groupName: item.groupName} });
    },
    goToSettingsPage() {
      this.$router.push({ path: 'settings'});
    },
    isDeleteGroupDisabled(item) {
      if (item.farm != this.currentFarm) return true;
      if (this.settings.userContext.contextName == 'ADMIN')
        return false;
      else if (this.settings.userContext.contextName == 'MANAGER') {
        let farms = this.settings.userContext.contextValue.split(',');
        if (farms.find(f => f == this.currentFarm))
        return false;
      }
      return true;
    },
    isUploadDisabled(item) {
      if (!this.isOnline) return true;
      if (item.samples == null) return true;
      if (item.samples.length === 0) return true;
      return false;
    },
    async saveGroup() {
      try {
        let groupName = `${this.editItem.farm}_${this.editItem.house}_${this.formatDate(this.editItem.date)}`;
        if(!this.editItem.clientUid) this.editItem.clientUid = crypto.randomUUID();
        const eggGroup = {
          deviceName: this.deviceName,
          clientUid: this.editItem.clientUid,
          groupName: groupName,
          operatorId: this.editItem.operatorId,
          farm: this.editItem.farm,
          house: this.editItem.house,
          date: this.editItem.date,
          notes: this.editItem.notes ? this.editItem.notes: '',
          samples: this.editItem.samples
        }

        if (this.editItem.oldGroupName == null){
          const items = this.eggGroups.filter(x => x.groupName === groupName);
          if (items.length > 0) {
            throw new Error(`Duplicate Group. ${groupName} already exists.`);
          }
          this.eggGroups.push(eggGroup);
        }
        else {
          const items = this.eggGroups.filter(x => x.groupName === groupName);
          if (this.editItem.oldGroupName !== groupName && items.length > 0) {
            throw new Error(`Duplicate Group. ${groupName} already exists.`);
          }
          const itemIndex = this.eggGroups.findIndex(x => x.groupName === this.editItem.oldGroupName);
          this.eggGroups[itemIndex] = eggGroup;
          // deep clone to force computed values to reset
          this.eggGroups = JSON.parse(JSON.stringify(this.eggGroups));
        }
        await OfflineStorage.setItem('eggGroups', this.eggGroups);
        this.closeModifyDialog();
      } catch(error) {
        this.$root.showNoticeError(error.message);
      }
    },  
    navigateBack() {
      this.$router.replace({ path: '/' });
    },
    async removeOldEggGroups() {
      let numToRemove = 0;

      const oldGroups = this.archivedEggGroups.filter(group => {
        let now =  new Date();
        let diffDays = this.daysDiff(now, group.uploadDate);
        if (diffDays > 14) {
          numToRemove ++;
          return false;
        }
        return true;
      });
        
      if (numToRemove > 0) {
        this.archivedEggGroups = oldGroups;
        await OfflineStorage.setItem('archivedEggGroups', this.archivedEggGroups);
      }

      console.log(`Removed ${numToRemove} old Egg Groups.`); // eslint-disable-line no-console
    },
    toAlphaNumOnly(value) {
      if (value == null) return;
      value = value.toUpperCase();
      value = value.replace(/[^A-Z0-9\s!?]/g,'');
      return value;
    },
    toggleCardDetails(index) {
      this.showDetail[index] = !this.showDetail[index];
      this.$forceUpdate();
    },
    async uploadEggGroup() {
     try {
        if (this.isBusy) return; // avoid duplicate uploads (possible double click)
        this.isBusy = true;
        this.showUploadDialog = false;
        await this.removeOldEggGroups();

        this.selectedGroup.deviceName = this.deviceName; // update in case name changed.
        
        if (this.selectedGroup.clientUid == null) { // shouldn't happen unless data created before version 1.0.1
          this.selectedGroup.clientUid = crypto.randomUUID();
          await OfflineStorage.setItem('eggGroups', this.eggGroups);
        }

        var uploadDateTime = await eggApi.addEggGroup(this.selectedGroup);

        //* Add group to archived groups.
        this.selectedGroup.uploadDate = uploadDateTime;
        this.archivedEggGroups.push(this.selectedGroup);
        await OfflineStorage.setItem('archivedEggGroups', this.archivedEggGroups);

        //* Remove group from active groups.
        const ndx = this.eggGroups.findIndex(e => e === this.selectedGroup);
        this.eggGroups.splice(ndx, 1);
        await OfflineStorage.setItem('eggGroups', this.eggGroups);
        this.$root.showNoticeSuccess(`Egg Group uploaded successfully.`);
      } catch(error) {
        console.log(error); // eslint-disable-line no-console
        this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    }
  }
}
</script>
<style scoped>
  .side-by-side li {
    display: block;
    float: left;
    width: 140px;
    margin: 2px;
  }
  .single li {
    display: block;
    float: left;
    margin: 2px;
  }
  .truncate {
    max-width: 215px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>